/**
 * Gradient Map Engine – DreamMee
 *
 * Deterministic, pixel-level recoloring using luminance-driven gradient mapping.
 * Converts grayscale source images into any color tone by mapping pixel brightness
 * to a 5-stop gradient (shadow → darkMid → mid → lightMid → highlight).
 *
 * Backward-compatible: if darkMid/lightMid are missing, they are interpolated
 * automatically from the 3-stop (shadow/mid/highlight) values.
 *
 * No AI, no diffusion — just luminance extraction + interpolation.
 *
 * Performance: ~3-5ms for a 460×560 image on modern hardware.
 */

const GradientMap = (() => {

  /**
   * Convert a hex color string to { r, g, b } (0-255).
   */
  function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return {
      r: parseInt(h.substring(0, 2), 16),
      g: parseInt(h.substring(2, 4), 16),
      b: parseInt(h.substring(4, 6), 16)
    };
  }

  /**
   * Linear interpolation between two values.
   */
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  /**
   * Interpolate two hex colors at t=0.5 (midpoint).
   * Used to auto-generate darkMid/lightMid for legacy 3-stop presets.
   */
  function interpolateHex(hex1, hex2) {
    const a = hexToRgb(hex1);
    const b = hexToRgb(hex2);
    const r = Math.round((a.r + b.r) / 2);
    const g = Math.round((a.g + b.g) / 2);
    const bl = Math.round((a.b + b.b) / 2);
    return '#' + [r, g, bl].map(c => c.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Build a 256-entry lookup table for a 5-stop gradient.
   * This avoids per-pixel interpolation — O(1) per pixel after build.
   *
   * 4 segments:
   *   Lum   0- 63: shadow  → darkMid
   *   Lum  64-127: darkMid → mid
   *   Lum 128-191: mid     → lightMid
   *   Lum 192-255: lightMid→ highlight
   *
   * @param {Object} gradient - { shadow, darkMid, mid, lightMid, highlight }
   * @returns {Uint8Array} - 768-entry array [R0,G0,B0, R1,G1,B1, ... R255,G255,B255]
   */
  function buildLUT(gradient) {
    // Backward compatibility: auto-generate darkMid/lightMid if missing
    const g = normalizeGradient(gradient);

    const stops = [
      hexToRgb(g.shadow),
      hexToRgb(g.darkMid),
      hexToRgb(g.mid),
      hexToRgb(g.lightMid),
      hexToRgb(g.highlight)
    ];

    const lut = new Uint8Array(256 * 3);
    const segmentSize = 64; // 256 / 4 segments

    for (let i = 0; i < 256; i++) {
      // Determine which segment this luminance falls into
      const seg = Math.min(Math.floor(i / segmentSize), 3);
      const localT = (i - seg * segmentSize) / (segmentSize - (seg === 3 ? 0 : 1));
      const t = Math.min(localT, 1);

      const from = stops[seg];
      const to = stops[seg + 1];

      const idx = i * 3;
      lut[idx]     = Math.round(lerp(from.r, to.r, t));
      lut[idx + 1] = Math.round(lerp(from.g, to.g, t));
      lut[idx + 2] = Math.round(lerp(from.b, to.b, t));
    }

    return lut;
  }

  /**
   * Normalize a gradient object — fill in missing darkMid/lightMid
   * for backward compatibility with 3-stop presets.
   */
  function normalizeGradient(gradient) {
    return {
      shadow: gradient.shadow,
      darkMid: gradient.darkMid || interpolateHex(gradient.shadow, gradient.mid),
      mid: gradient.mid,
      lightMid: gradient.lightMid || interpolateHex(gradient.mid, gradient.highlight),
      highlight: gradient.highlight
    };
  }

  // LUT cache: keyed by all 5 hex values
  const lutCache = new Map();

  function getCachedLUT(gradient) {
    const g = normalizeGradient(gradient);
    const key = `${g.shadow}|${g.darkMid}|${g.mid}|${g.lightMid}|${g.highlight}`;
    if (lutCache.has(key)) return lutCache.get(key);
    const lut = buildLUT(gradient);
    lutCache.set(key, lut);
    return lut;
  }

  /**
   * Apply a gradient map to an image, returning a recolored canvas.
   *
   * @param {HTMLImageElement} img - Source image (ideally grayscale, but works on any)
   * @param {Object} gradient - { shadow, [darkMid], mid, [lightMid], highlight }
   * @param {Object} [options] - Optional settings
   * @param {number} [options.blackThreshold=0] - Pixels with luminance <= this value are
   *   left untouched (preserving black outlines, shadows, etc.). Set to 0 to disable.
   *   Recommended value for car assets: 10-15.
   * @returns {HTMLCanvasElement} - Recolored canvas (usable as Konva.Image source)
   */
  function apply(img, gradient, options = {}) {
    const blackThreshold = options.blackThreshold || 0;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(img, 0, 0, w, h);
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;

    const lut = getCachedLUT(gradient);

    // Process all pixels
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      if (alpha === 0) continue; // skip fully transparent

      // Calculate luminance from source pixel (ITU-R BT.601)
      const lum = Math.round(data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);

      // Skip near-black pixels to preserve outlines and dark details
      if (lum <= blackThreshold) continue;

      // Look up the gradient color for this luminance
      const lutIdx = lum * 3;
      data[i]     = lut[lutIdx];
      data[i + 1] = lut[lutIdx + 1];
      data[i + 2] = lut[lutIdx + 2];
      // alpha stays unchanged
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  /**
   * Clear the LUT cache (e.g. if presets are changed dynamically).
   */
  function clearCache() {
    lutCache.clear();
  }

  // Public API
  return { apply, clearCache, hexToRgb, buildLUT, normalizeGradient, interpolateHex };

})();
